import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import * as Sep from '@radix-ui/react-separator'
import * as SA from '@radix-ui/react-scroll-area'
import { cn } from './cn'

/**
 * Surface container. Compose with CardHeader / CardBody / CardFooter, or pass `title` for the
 * common case: `<Card title="Pricing" actions={<Button size="sm">Edit</Button>}>…</Card>`.
 * `flush` removes body padding (tables, lists that run edge to edge).
 */
export const Card = forwardRef<HTMLDivElement, Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title?: ReactNode; description?: ReactNode; actions?: ReactNode; footer?: ReactNode; flush?: boolean; as?: 'div' | 'section'
}>(function Card({ title, description, actions, footer, flush, className, children, as = 'section', ...props }, ref) {
  const Tag = as
  const hasHeader = title !== undefined || actions !== undefined
  return (
    <Tag ref={ref as any} className={cn('flex min-w-0 flex-col rounded-card border border-border bg-surface shadow-card', className)} {...props}>
      {hasHeader && <CardHeader title={title} description={description} actions={actions} />}
      {children !== undefined && (flush ? children : <CardBody className={cn(hasHeader && 'pt-0')}>{children}</CardBody>)}
      {footer && <CardFooter>{footer}</CardFooter>}
    </Tag>
  )
})

export function CardHeader({ title, description, actions, className, children }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <div className={cn('flex items-start gap-3 px-4 pt-4 pb-3 sm:px-5', className)}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && <h2 className="text-base font-semibold text-text">{title}</h2>}
        {description && <p className="text-sm text-text-muted">{description}</p>}
        {children}
      </div>
      {actions && <div className="-my-1 flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('px-4 py-4 sm:px-5', className)}>{children}</div>
}

export function CardFooter({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex flex-wrap items-center justify-end gap-2 rounded-b-card border-t border-border bg-surface-sunken/50 px-4 py-3 sm:px-5', className)}>{children}</div>
}

/** A divided region inside a card (e.g. "Shipping address" under "Customer"). */
export function CardSection({ title, actions, className, children }: { title?: ReactNode; actions?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={cn('border-t border-border px-4 py-4 first:border-t-0 sm:px-5', className)}>
      {(title || actions) && (
        <div className="mb-2 flex items-center justify-between gap-2">
          {title && <h3 className="text-sm font-semibold tracking-[0.01em] text-text-muted">{title}</h3>}
          {actions}
        </div>
      )}
      {children}
    </div>
  )
}

/**
 * Settings-style section: title + description on the left, content on the right (stacks on
 * mobile). `<Section title="Store details" description="Shown on invoices">…cards…</Section>`
 */
export function Section({ title, description, actions, children, className, layout = 'split' }: {
  title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string
  /** 'split' (two columns ≥ 1024 px) or 'stacked' (heading above). */
  layout?: 'split' | 'stacked'
}) {
  return (
    <section className={cn(layout === 'split' ? 'grid gap-4 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] lg:gap-8' : 'flex flex-col gap-3', className)}>
      <div className="flex items-start justify-between gap-3 lg:flex-col lg:justify-start">
        <div className="flex flex-col gap-1">
          <h2 className="text-md font-semibold text-text">{title}</h2>
          {description && <p className="text-base text-text-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className="flex min-w-0 flex-col gap-4">{children}</div>
    </section>
  )
}

/** Hairline divider. */
export function Separator({ orientation = 'horizontal', className }: { orientation?: 'horizontal' | 'vertical'; className?: string }) {
  return <Sep.Root orientation={orientation} className={cn('shrink-0 bg-border', orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px', className)} />
}

/** Custom-styled scroll region (thin overlay scrollbars) for fixed-height panels. */
export function ScrollArea({ className, viewportClassName, children, orientation = 'vertical' }: { className?: string; viewportClassName?: string; children: ReactNode; orientation?: 'vertical' | 'horizontal' | 'both' }) {
  const bar = 'flex touch-none select-none p-1 transition-colors duration-150'
  return (
    <SA.Root type="hover" className={cn('relative overflow-hidden', className)}>
      <SA.Viewport className={cn('size-full rounded-[inherit]', viewportClassName)}>{children}</SA.Viewport>
      {orientation !== 'horizontal' && (
        <SA.Scrollbar orientation="vertical" className={cn(bar, 'h-full w-3')}><SA.Thumb className="relative flex-1 rounded-full bg-border-strong" /></SA.Scrollbar>
      )}
      {orientation !== 'vertical' && (
        <SA.Scrollbar orientation="horizontal" className={cn(bar, 'h-3 flex-col')}><SA.Thumb className="relative flex-1 rounded-full bg-border-strong" /></SA.Scrollbar>
      )}
      <SA.Corner />
    </SA.Root>
  )
}
